import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { MasterType } from '@/entities/settlementstatisticsconverter/lib/master-type';
import { AutocompleteField, Button, Form } from '@/shared/ui';
import { SideMenuFormFooter } from '@/widgets/right-side-menu/ui/SideMenuFormFooter';
import type { MasterRow } from '../master-table/with-deletable-flag';
import {
  buildCodeOptions,
  buildNameOptions,
  computeVisibleLevels,
  findCodeByName,
  findNameByCode,
} from './build-master-autocomplete-options';
import {
  createMasterCreateSchema,
  createMasterEditSchema,
  type MasterCreateFormValues,
} from './schema';
import { useMasterCreateMutation } from './use-master-create-mutation';
import { useMasterEditMutation } from './use-master-edit-mutation';

type MasterCreateFormProps = {
  convertResultId: string;
  masterType: MasterType;
  /** 既存マスタデータ（Autocomplete候補用） */
  existingData: MasterRow[];
  /** 編集対象（undefinedなら新規追加モード） */
  editTarget?: MasterRow;
  onClose: () => void;
};

type Level = 1 | 2 | 3 | 4;

/** 末端レベルを判定する（最も深い値が存在するレベル） */
const getLeafLevel = (row: MasterRow): Level => {
  if (row.level4_code) return 4;
  if (row.level3_code) return 3;
  if (row.level2_code) return 2;
  return 1;
};

/** 編集対象から表示するレベルを判定する */
const getEditVisibleLevels = (row: MasterRow): Record<Level, boolean> => ({
  1: true,
  2: !!row.level2_code,
  3: !!row.level3_code,
  4: !!row.level4_code,
});

/**
 * マスタ新規追加・編集フォーム
 *
 * editTarget がない場合: 新規追加モード（既存動作）
 * editTarget がある場合: 編集モード（末端名称のみ編集可能）
 */
export const MasterCreateForm: FC<MasterCreateFormProps> = ({
  convertResultId,
  masterType,
  existingData,
  editTarget,
  onClose,
}) => {
  const { t } = useTranslation();
  const isEditMode = !!editTarget;
  const leafLevel = editTarget ? getLeafLevel(editTarget) : null;

  const form = useForm<MasterCreateFormValues>({
    resolver: zodResolver(
      isEditMode && leafLevel
        ? createMasterEditSchema(leafLevel)
        : createMasterCreateSchema(existingData),
    ),
    defaultValues: editTarget
      ? {
          level1_code: editTarget.level1_code ?? '',
          level1_name: editTarget.level1_name ?? '',
          level2_code: editTarget.level2_code ?? '',
          level2_name: editTarget.level2_name ?? '',
          level3_code: editTarget.level3_code ?? '',
          level3_name: editTarget.level3_name ?? '',
          level4_code: editTarget.level4_code ?? '',
          level4_name: editTarget.level4_name ?? '',
        }
      : {
          level1_code: '',
          level1_name: '',
          level2_code: '',
          level2_name: '',
          level3_code: '',
          level3_name: '',
          level4_code: '',
          level4_name: '',
        },
  });

  const watchedCodes = {
    level1_code: form.watch('level1_code'),
    level2_code: form.watch('level2_code'),
    level3_code: form.watch('level3_code'),
  };

  // 新規追加モード: 既存コードかどうかで次レベルの表示を判定
  // 編集モード: editTarget の値が存在するレベルを全て表示
  const levelVisibility: Record<Level, boolean> = isEditMode
    ? getEditVisibleLevels(editTarget)
    : (() => {
        const { showLevel2, showLevel3, showLevel4 } = computeVisibleLevels({
          existingRows: existingData,
          formData: watchedCodes,
        });
        return { 1: true, 2: showLevel2, 3: showLevel3, 4: showLevel4 };
      })();

  const createMutation = useMasterCreateMutation({
    resultId: convertResultId,
    masterType,
    onSuccess: onClose,
  });

  const editMutation = useMasterEditMutation({
    resultId: convertResultId,
    masterType,
    masterId: editTarget?.id ?? '',
    onSuccess: onClose,
  });

  const handleSubmit = (values: MasterCreateFormValues) => {
    if (isEditMode && leafLevel) {
      const leafNameField = `level${leafLevel}_name` as keyof MasterCreateFormValues;
      editMutation.mutate(values[leafNameField] ?? '');
    } else {
      createMutation.mutate({
        convertResultId,
        data: {
          level1_code: values.level1_code,
          level1_name: values.level1_name,
          level2_code: values.level2_code || undefined,
          level2_name: values.level2_name || undefined,
          level3_code: values.level3_code || undefined,
          level3_name: values.level3_name || undefined,
          level4_code: values.level4_code || undefined,
          level4_name: values.level4_name || undefined,
        },
      });
    }
  };

  const isPending = isEditMode ? editMutation.isPending : createMutation.isPending;

  // 各レベルのオプションを構築（新規追加モードのみ使用）
  const levelOptions = ([1, 2, 3, 4] as const).map((level) => ({
    level,
    codeOptions: buildCodeOptions({ rows: existingData, level, parentCodes: watchedCodes }),
    nameOptions: buildNameOptions({ rows: existingData, level, parentCodes: watchedCodes }),
  }));

  // 下位レベルのフォーム値をリセットする
  const resetLowerLevels = (level: Level) => {
    for (const l of ([2, 3, 4] as const).filter((l) => l > level)) {
      form.setValue(`level${l}_code` as const, '', { shouldDirty: false });
      form.setValue(`level${l}_name` as const, '', { shouldDirty: false });
      form.clearErrors([`level${l}_code`, `level${l}_name`]);
    }
  };

  // コード選択時に名称を自動入力するハンドラ
  const handleCodeChange = (level: Level, code: string) => {
    const name = findNameByCode({ rows: existingData, level, code, parentCodes: watchedCodes });
    if (name) {
      form.setValue(`level${level}_name` as const, name, { shouldDirty: true });
    }
    resetLowerLevels(level);
  };

  // 名称選択時にコードを自動入力するハンドラ
  const handleNameChange = (level: Level, name: string) => {
    const code = findCodeByName({ rows: existingData, level, name, parentCodes: watchedCodes });
    if (code) {
      form.setValue(`level${level}_code` as const, code, { shouldDirty: true });
    }
    resetLowerLevels(level);
  };

  /** 編集モード時のフィールド disabled 判定 */
  const isFieldDisabled = (level: Level, field: 'code' | 'name'): boolean => {
    if (!isEditMode) return false;
    // 末端の名称フィールドのみ編集可能
    return !(level === leafLevel && field === 'name');
  };

  return (
    <Form.Provider {...form}>
      <Form
        leavePageAlertCondition={{
          dirtyFields: form.formState.dirtyFields,
          isSubmitting: form.formState.isSubmitting,
          isSubmitSuccessful: form.formState.isSubmitSuccessful,
        }}
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-4">
          {levelOptions.map(({ level, codeOptions, nameOptions }) => {
            if (!levelVisibility[level]) return null;
            return (
              <div key={level} className="flex gap-2">
                <AutocompleteField
                  control={form.control}
                  name={`level${level}_code` as keyof MasterCreateFormValues}
                  className="w-[40%]"
                  label={t('settlement-statistics-converter.master.level-code', { level })}
                  options={codeOptions}
                  showOnFocus
                  required
                  disabled={isFieldDisabled(level, 'code')}
                  onValueChange={(code) => handleCodeChange(level, code)}
                />
                <AutocompleteField
                  control={form.control}
                  name={`level${level}_name` as keyof MasterCreateFormValues}
                  label={t('settlement-statistics-converter.master.level-name', { level })}
                  options={nameOptions}
                  showOnFocus
                  required
                  disabled={isFieldDisabled(level, 'name')}
                  onValueChange={(name) => handleNameChange(level, name)}
                />
              </div>
            );
          })}
        </div>

        <SideMenuFormFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {t('common.actions.save')}
          </Button>
        </SideMenuFormFooter>
      </Form>
    </Form.Provider>
  );
};
